import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle } from "lucide-react";

export default function AIDraggableChatButton({ onOpen }) {
  const BUTTON_SIZE = 48;
  const SNAP_MARGIN = 16;

  // Initial position (Bottom Right)
  const [position, setPosition] = useState({
    x: window.innerWidth - BUTTON_SIZE - 24,
    y: window.innerHeight - BUTTON_SIZE - 100,
  });

  const [isDragging, setIsDragging] = useState(false);

  const dragRef = useRef(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const draggedRef = useRef(false);

  // --- Drag Logic ---

  const startDrag = useCallback((clientX, clientY) => {
    const rect = dragRef.current.getBoundingClientRect();
    offsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    draggedRef.current = false;
    setIsDragging(true);
  }, []);

  const moveDrag = useCallback(
    (clientX, clientY) => {
      if (!isDragging) return;

      draggedRef.current = true;

      const newX = clientX - offsetRef.current.x;
      const newY = clientY - offsetRef.current.y;

      const maxX = window.innerWidth - BUTTON_SIZE;
      const maxY = window.innerHeight - BUTTON_SIZE;

      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    },
    [isDragging]
  );

  const stopDrag = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    // Snap to the nearest horizontal edge (Left or Right)
    setPosition((prev) => {
      const middle = window.innerWidth / 2;
      const snapX =
        prev.x < middle
          ? SNAP_MARGIN
          : window.innerWidth - BUTTON_SIZE - SNAP_MARGIN;

      return { ...prev, x: snapX };
    });
  }, [isDragging]);

  // --- Interaction Handler ---

  const handleClick = useCallback((e) => {
    // If we just finished dragging, don't click
    if (draggedRef.current) return;
    // Trigger the Dashboard's chat modal
    if (onOpen) onOpen();
  }, [onOpen]);

  // --- Event Listeners ---

  // Mouse Events (Desktop)
  useEffect(() => {
    const handleMove = (e) => moveDrag(e.clientX, e.clientY);
    const handleUp = () => stopDrag();

    if (isDragging) {
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [isDragging, moveDrag, stopDrag]);

  // Touch Events (Mobile)
  useEffect(() => {
    const handleMove = (e) => {
      // Prevent scrolling while dragging on mobile
      e.preventDefault();
      if (e.touches.length > 0) {
        moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => stopDrag();

    if (isDragging) {
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);
    }

    return () => {
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, moveDrag, stopDrag]);

  return (
    <div
      ref={dragRef}
      onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
      onTouchStart={(e) => {
        if (e.touches.length > 0) {
          startDrag(e.touches[0].clientX, e.touches[0].clientY);
        }
      }}
      onClick={handleClick}
      className="fixed z-50 select-none group"
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? "grabbing" : "grab",
        transition: isDragging ? "none" : "left 0.25s ease, top 0.25s ease",
      }}
    >
      <div
        className={`w-12 h-12 rounded-full 
        bg-gradient-to-br from-blue-500 to-purple-600 
        dark:from-blue-400 dark:to-purple-500
        shadow-lg dark:shadow-xl 
        flex items-center justify-center 
        transition-transform duration-200 relative
        border border-white/30 dark:border-white/10
        ${isDragging ? "scale-110" : "hover:scale-105 active:scale-95"}`}
      >
        <MessageCircle size={21} className="text-white" />
        
        {/* Tooltip (Desktop Only) */}
        <span className="absolute right-14 top-1/2 -translate-y-1/2 
                       bg-white dark:bg-gray-800 text-gray-700 dark:text-white text-xs font-medium px-2 py-1 rounded 
                       opacity-0 group-hover:opacity-100 transition-opacity duration-200 
                       whitespace-nowrap shadow-lg dark:shadow-xl pointer-events-none border border-gray-200 dark:border-gray-700
                       hidden md:block">
          AI Chat
        </span>

        {/* Mobile Tag */}
        <span className="absolute -left-8 top-1/2 -translate-y-1/2 
                       bg-white/80 dark:bg-black/40 backdrop-blur-sm text-gray-700 dark:text-white text-[10px] font-bold px-1.5 py-0.5 rounded
                       whitespace-nowrap shadow-sm pointer-events-none 
                       block md:hidden border border-gray-200 dark:border-white/10">
          AI
        </span>
      </div>
    </div>
  );
}